import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// H-01: passenger portal migrated to httpOnly cookie — no JWT in localStorage.
// TOKEN_KEYS kept as empty export so consumers don't break; remove usages over time.
export const TOKEN_KEYS = {};

// Clear legacy tokens from localStorage on load
(() => {
  localStorage.removeItem('safer_admin_token');
  localStorage.removeItem('token');
  localStorage.removeItem('safer_passenger_token'); // H-01: was passenger JWT, now cookie-based
  localStorage.removeItem('passenger_token');
  localStorage.removeItem('safer_office_token'); // M-05: migrated to httpOnly cookie
})();

// 60 s to survive Render free-tier cold starts (30-60 s wake-up time)
// withCredentials: true — sends the httpOnly safer_session cookie cross-origin
const api = axios.create({ baseURL: API_URL, timeout: 60000, withCredentials: true });

// Auth is cookie-based for all portals — safer_session httpOnly cookie sent automatically via withCredentials.

// This instance is shared across portals for endpoints that exist once but
// serve everyone (notifications, push subscriptions) — a browser that's
// logged into more than one portal at once carries more than one session
// cookie simultaneously, so the backend can't tell which identity a request
// means from the cookie alone. Tagging every request with the portal the
// current page belongs to lets middlewares/auth.js's sessionCookie() pick
// the right one instead of always defaulting to the passenger cookie.
api.interceptors.request.use((config) => {
  const p = window.location.pathname;
  if (p.startsWith('/transport-provider')) config.headers['X-Safer-Portal'] = 'transport-provider';
  else if (p.startsWith('/office')) config.headers['X-Safer-Portal'] = 'office';
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response?.status === 401) {
      const p = window.location.pathname;
      if (p.startsWith('/admin') && !p.startsWith('/admin/login')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login:           (data) => api.post('/auth/login', data),
  logout:          ()     => api.post('/auth/logout'),
  getProfile:      ()     => api.get('/auth/profile'),
  updateProfile:   (data) => api.put('/auth/profile', data),
  changePassword:  (data) => api.post('/auth/change-password', data),
  changeEmail:     (data) => api.post('/auth/change-email', data),
  confirmEmailChange: (data) => api.post('/auth/confirm-email', data),
  forgotPassword:  (data) => api.post('/auth/forgot-password', data),
  resetPassword:   (data) => api.post('/auth/reset-password', data),
  // Passenger phone + WhatsApp OTP — no password. requestOtp covers both
  // login and registration; verifyOtp creates the account if the phone is new.
  requestOtp:      (data) => api.post('/auth/otp/request', data),
  verifyOtp:       (data) => api.post('/auth/otp/verify', data),
  // WhatsApp phone verification (passenger registration + PIN) — see
  // backend/src/services/verificationService.js.
  startWhatsAppVerification:     (data) => api.post('/auth/whatsapp/start', data),
  getWhatsAppVerificationStatus: (id)   => api.get(`/auth/whatsapp/status/${id}`),
  completeWhatsAppRegistration:  (data) => api.post('/auth/whatsapp/complete-registration', data),
  // Phone + PIN login (returning passenger) + forgot-PIN recovery.
  pinLogin:        (data) => api.post('/auth/pin/login', data),
  startForgotPin:  (data) => api.post('/auth/pin/forgot/start', data),
  completeForgotPin: (data) => api.post('/auth/pin/forgot/complete', data),
  // Google Play/App Store account-deletion requirement (soft-delete:
  // isActive=false + PII anonymized server-side). pin required only for
  // accounts that have one set.
  deleteAccount:   (data) => api.post('/auth/delete-account', data),
};

export const tripAPI = {
  getCities: ()       => api.get('/trips/cities'),
  search: (params)    => api.get('/trips/search', { params }),
  getById: (id) => api.get(`/trips/${id}`),
  getPaymentInfo: (id) => api.get(`/trips/${id}/payment-info`),
  getCompanyTrips: () => api.get('/trips/company/all'),
  getGroupSuggestions: (params) => api.get('/trips/group-suggestions', { params }),
  getGroupSeatSuggestion: (id, passengerCount) => api.get(`/trips/${id}/group-suggestion`, { params: { passengerCount } }),
  create: (data) => api.post('/trips', data),
  update: (id, data) => api.put(`/trips/${id}`, data),
  suspend: (id) => api.patch(`/trips/${id}/suspend`),
  cancel: (id) => api.patch(`/trips/${id}/cancel`),
  getPassengers: (id) => api.get(`/trips/${id}/passengers`),
  getManifestPdf: (id) => api.get(`/trips/${id}/manifest`, { responseType: 'blob' }),
  sendDepartureReminder: (id) => api.post(`/trips/${id}/departure-reminder`),
  setAvailableSeats: (id, seats) => api.patch(`/trips/${id}/available-seats`, { availableSeats: seats }),
  updateBlockedSeats: (id, seats) => api.patch(`/trips/${id}/blocked-seats`, { blockedSeats: seats }),
  updateGps: (id, data) => api.patch(`/trips/${id}/gps`, data),
};

export const boardingAPI = {
  checkIn: (bookingNumber) => api.post(`/bookings/${bookingNumber}/check-in`),
  scanBoard: (qrData) => api.post('/bookings/scan-board', { qrData }),
};

export const busAPI = {
  getAll:  ()       => api.get('/buses'),
  create:  (fd)     => api.post('/buses', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update:  (id, fd) => api.put(`/buses/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete:  (id)     => api.delete(`/buses/${id}`),
};

export const seatTemplateAPI = {
  getAll:  ()           => api.get('/seat-templates'),
  create:  (data)       => api.post('/seat-templates', data),
  update:  (id, data)   => api.put(`/seat-templates/${id}`, data),
  delete:  (id)         => api.delete(`/seat-templates/${id}`),
};

export const vehicleTypeAPI = {
  getAll:  (includeInactive = false) => api.get('/vehicle-types', { params: includeInactive ? { includeInactive: 'true' } : {} }),
  create:  (fd)     => api.post('/vehicle-types', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update:  (id, fd) => api.put(`/vehicle-types/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete:  (id)      => api.delete(`/vehicle-types/${id}`),
};

export const bookingAPI = {
  getMine: () => api.get('/bookings/my'),
  getPending: () => api.get('/bookings/pending'),
  confirm: (id) => api.patch(`/bookings/${id}/confirm`),
  reject: (id, data) => api.patch(`/bookings/${id}/reject`, data),
  createOffice: (data) => api.post('/bookings/office', data),
};

export const waitingListAPI = {
  join: (data) => api.post('/waiting-list', data),
  getMine: () => api.get('/waiting-list/my'),
};

export const financeAPI = {
  getDashboard: () => api.get('/finance/dashboard'),
  getReport: (params) => api.get('/finance/report', { params }),
};

export const companyAPI = {
  register: (data) => api.post('/companies/register', data),
  getAll: (params) => api.get('/companies', { params }),
  getById: (id) => api.get(`/companies/${id}`),
  update: (id, data) => api.put(`/companies/${id}`, data),
  approve: (id) => api.patch(`/companies/${id}/approve`),
  toggleStatus: (id) => api.patch(`/companies/${id}/toggle-status`),
  getPlatformStats: () => api.get('/companies/platform-stats'),
  setCommission: (id, data) => api.patch(`/companies/${id}/commission`, data),
};

export const userAPI = {
  getStaff: (params) => api.get('/users/staff', { params }),
  createStaff: (data) => api.post('/users/staff', data),
  updateStaff: (id, data) => api.put(`/users/staff/${id}`, data),
  toggleStaff: (id) => api.patch(`/users/staff/${id}/toggle`),
  getPassengers: (params) => api.get('/users/passengers', { params }),
  togglePassenger: (id) => api.patch(`/users/passengers/${id}/toggle`),
  getAllBookings: (params) => api.get('/users/all-bookings', { params }),
};

export const verifyAPI = {
  check: (bookingNumber, sig) => api.get(`/bookings/verify/${bookingNumber}`, { params: sig ? { sig } : {} }),
};

export const notificationAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  markRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllRead: (params) => api.patch('/notifications/read-all', null, { params }),
};

export const pushAPI = {
  getVapidPublicKey: () => api.get('/push/vapid-public-key'),
  subscribe:   (subscription) => api.post('/push/subscribe', subscription),
  unsubscribe: (endpoint)     => api.post('/push/unsubscribe', { endpoint }),
};

export const cityCodeAPI = {
  getAll:  ()           => api.get('/city-codes'),
  create:  (data)       => api.post('/city-codes', data),
  update:  (id, data)   => api.put(`/city-codes/${id}`, data),
  remove:  (id)         => api.delete(`/city-codes/${id}`),
};

export const popularRouteAPI = {
  getAll:      (adminView) => api.get('/popular-routes', { params: adminView ? { all: 'true' } : {} }),
  create:      (data)      => api.post('/popular-routes', data),
  update:      (id, data)  => api.put(`/popular-routes/${id}`, data),
  remove:      (id)        => api.delete(`/popular-routes/${id}`),
  uploadImage: (id, file)  => {
    const fd = new FormData();
    fd.append('image', file);
    return api.post(`/popular-routes/${id}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export const promoAPI = {
  getAll:   ()         => api.get('/promos'),
  getMine:  ()         => api.get('/promos/mine'),
  validate: (code)     => api.post('/promos/validate', { code }),
  create:   (data)     => api.post('/promos', data),
  update:   (id, data) => api.put(`/promos/${id}`, data),
  remove:   (id)       => api.delete(`/promos/${id}`),
};

export const settlementAPI = {
  getStats:         ()       => api.get('/settlements/stats'),
  getOverview:      (params) => api.get('/settlements/overview', { params }),
  getAgencies:      (params) => api.get('/settlements/agencies', { params }),
  getTransportOverview: (params) => api.get('/settlements/transport-overview', { params }),
  markPaid:         (data)   => api.post('/settlements/pay', data),
  revertPaid:       (id)     => api.patch(`/settlements/${id}/unpay`),
  getCompanyBookings:  (params)      => api.get('/settlements/company-bookings', { params }),
  exportPdf:           (params)      => api.get('/settlements/export-pdf', { params, responseType: 'blob' }),
  getMyObligations:    (params)      => api.get('/settlements/my-obligations', { params }),
  submitPayment:       (formData)    => api.post('/settlements/submit-payment', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  confirmPayment:      (id)          => api.patch(`/settlements/${id}/confirm`),
  rejectPayment:       (id, data)    => api.patch(`/settlements/${id}/reject`, data),
};

// ── Office Portal API (separate axios instance — httpOnly cookie auth) ────────
// M-05: migrated from localStorage Bearer token to safer_office_session httpOnly cookie.
// withCredentials sends the cookie cross-origin (Netlify → Render).
const officeApi = axios.create({ baseURL: API_URL, timeout: 60000, withCredentials: true });

officeApi.interceptors.response.use(
  (res) => res,
  (error) => {
    const p = window.location.pathname;
    // Exclude the login page itself — otherwise the office/auth/me check that
    // OfficeAuthProvider fires on mount 401s for every logged-out visitor,
    // and this handler force-navigates to the page that's already loaded,
    // which reloads it, re-fires the same check, and loops.
    if (error.response?.status === 401 && p.startsWith('/office') && !p.startsWith('/office/login')) {
      window.location.href = '/office/login';
    }
    return Promise.reject(error);
  }
);

export const officeAPI = {
  // Auth
  login:           (data)       => officeApi.post('/office/auth/login', data),
  logout:          ()           => officeApi.post('/office/auth/logout'),
  me:              ()           => officeApi.get('/office/auth/me'),
  // Trips
  getCities:       ()           => officeApi.get('/office/trips/cities'),
  searchTrips:     (params)     => officeApi.get('/office/trips/search', { params }),
  getTripSeats:    (id)         => officeApi.get(`/office/trips/${id}/seats`),
  // Customers
  lookupCustomer:  (data)       => officeApi.post('/office/customers/lookup', data),
  // Bookings
  createBooking:      (data)       => officeApi.post('/office/bookings', data),
  submitPaymentSlip:  (id, fd)     => officeApi.post(`/office/bookings/${id}/submit-payment`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getBookings:        (params)     => officeApi.get('/office/bookings', { params }),
  getBookingById:  (id)         => officeApi.get(`/office/bookings/${id}`),
  cancelBooking:   (id, data)   => officeApi.post(`/office/bookings/${id}/cancel`, data),
  // Dashboard
  getDashboard:    ()           => officeApi.get('/office/dashboard/today'),
  getCommission:   (params)     => officeApi.get('/office/commission/summary', { params }),
  // Parcel Delivery (Smart Transportation Assisted Parcel Delivery System)
  lookupParcel:            (params)      => officeApi.get('/office/parcels/lookup', { params }),
  getOfficeParcels:        (scope)       => officeApi.get('/office/parcels', { params: { scope } }),
  createParcel:            (data)        => officeApi.post('/office/parcels', data),
  updateParcel:            (id, data)    => officeApi.put(`/office/parcels/${id}`, data),
  submitParcelPaymentSlip: (id, fd)      => officeApi.post(`/office/parcels/${id}/submit-payment`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  verifyAndReceiveParcel:  (id, data)    => officeApi.post(`/office/parcels/${id}/verify`, data),
  getParcelLabel:          (id)          => officeApi.get(`/office/parcels/${id}/label`, { responseType: 'blob' }),
  rejectParcelAtDropoff:   (id, data)    => officeApi.post(`/office/parcels/${id}/reject`, data),
  approveParcelLateException: (id, data) => officeApi.post(`/office/parcels/${id}/late-exception`, data),
  collectAdditionalPayment: (id, data)   => officeApi.post(`/office/parcels/${id}/additional-payment`, data),
  markParcelLoaded:        (id)          => officeApi.patch(`/office/parcels/${id}/loaded`),
  markParcelInTransit:     (id)          => officeApi.patch(`/office/parcels/${id}/in-transit`),
  markParcelArrived:       (id)          => officeApi.patch(`/office/parcels/${id}/arrived`),
  markParcelReady:         (id)          => officeApi.patch(`/office/parcels/${id}/ready`),
  markParcelCollected:     (id, otp)     => officeApi.post(`/office/parcels/${id}/collect`, { otp }),
  reportParcelIncident:    (id, fd)      => officeApi.post(`/office/parcels/${id}/incidents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// ── Transport Provider Portal API (separate axios instance — httpOnly cookie
// auth) — mirrors officeApi/officeAPI exactly, one org type over. ─────────────
const transportProviderApi = axios.create({ baseURL: API_URL, timeout: 60000, withCredentials: true });

transportProviderApi.interceptors.response.use(
  (res) => res,
  (error) => {
    const p = window.location.pathname;
    if (error.response?.status === 401 && p.startsWith('/transport-provider') && !p.startsWith('/transport-provider/login')) {
      window.location.href = '/transport-provider/login';
    }
    return Promise.reject(error);
  }
);

export const transportProviderAPI = {
  // Public registration + auth
  register: (formData) => transportProviderApi.post('/transport-provider/register', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  login:  (data) => transportProviderApi.post('/transport-provider/auth/login', data),
  logout: ()     => transportProviderApi.post('/transport-provider/auth/logout'),
  me:     ()     => transportProviderApi.get('/transport-provider/auth/me'),
  updateMyLocation: (lat, lng) => transportProviderApi.patch('/transport-provider/me/location', { lat, lng }),
  getMyCommissionSummary: (params) => transportProviderApi.get('/transport-provider/me/commission-summary', { params }),
  // Fleet
  getVehicles: ()       => transportProviderApi.get('/transport-provider/vehicles'),
  addVehicle:  (data)   => transportProviderApi.post('/transport-provider/vehicles', data),
  updateVehicle: (id, data) => transportProviderApi.patch(`/transport-provider/vehicles/${id}`, data),
  toggleVehicle: (id)   => transportProviderApi.patch(`/transport-provider/vehicles/${id}/toggle`),
  // Staff (owner only)
  getStaff:    ()       => transportProviderApi.get('/transport-provider/staff'),
  addStaff:    (data)   => transportProviderApi.post('/transport-provider/staff', data),
  toggleStaff: (staffId) => transportProviderApi.patch(`/transport-provider/staff/${staffId}/toggle`),
  // Job board / quoting
  getOpenRequests: ()          => transportProviderApi.get('/transport-provider/requests/open'),
  submitQuote:     (requestId, data) => transportProviderApi.post(`/transport-provider/requests/${requestId}/quotes`, data),
  getMyQuotes:     ()          => transportProviderApi.get('/transport-provider/quotes'),
  // Orders
  getOrders:        ()           => transportProviderApi.get('/transport-provider/orders'),
  getOrderById:     (id)         => transportProviderApi.get(`/transport-provider/orders/${id}`),
  assignVehicle:    (id, data)   => transportProviderApi.post(`/transport-provider/orders/${id}/assign`, data),
  confirmPickup:    (id, otp)    => transportProviderApi.post(`/transport-provider/orders/${id}/confirm-pickup`, { otp }),
  markInTransit:    (id)         => transportProviderApi.patch(`/transport-provider/orders/${id}/in-transit`),
  confirmDelivery:  (id, otp)    => transportProviderApi.post(`/transport-provider/orders/${id}/confirm-delivery`, { otp }),
  // Ride job board / offers (same provider account, separate job board)
  getOpenRideRequests: ()          => transportProviderApi.get('/transport-provider/rides/requests/open'),
  submitRideOffer:     (requestId, data) => transportProviderApi.post(`/transport-provider/rides/requests/${requestId}/offers`, data),
  getMyRideOffers:     ()          => transportProviderApi.get('/transport-provider/rides/offers'),
  getRideOrders:       ()          => transportProviderApi.get('/transport-provider/rides/orders'),
  getRideOrderById:    (id)        => transportProviderApi.get(`/transport-provider/rides/orders/${id}`),
  markRideEnRoute:     (id)        => transportProviderApi.patch(`/transport-provider/rides/orders/${id}/en-route`),
  markRideArrived:     (id)        => transportProviderApi.patch(`/transport-provider/rides/orders/${id}/arrived`),
  markRideInProgress:  (id)        => transportProviderApi.patch(`/transport-provider/rides/orders/${id}/in-progress`),
  markRideCompleted:   (id)        => transportProviderApi.patch(`/transport-provider/rides/orders/${id}/complete`),
};

export const parcelAPI = {
  // Smart suggestions (public)
  getVehicleSuggestions: (weightKg)       => api.get('/parcels/vehicle-suggestions', { params: { weightKg } }),
  getTrips:              (params)         => api.get('/parcels/trips', { params }),
  getQuote:               (params)        => api.get('/parcels/quote', { params }),
  getOffices:             (companyId, city) => api.get('/parcels/offices', { params: { ...(companyId ? { companyId } : {}), ...(city ? { city } : {}) } }),
  track:                  (trackingNumber) => api.get(`/parcels/track/${trackingNumber}`),
  // Passenger self-service
  create:                 (data)          => api.post('/parcels', data),
  assignTrip:              (id, data)     => api.patch(`/parcels/${id}/assign-trip`, data),
  uploadPaymentProof:      (id, fd)       => api.post(`/parcels/${id}/payment-proof`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getMine:                 ()             => api.get('/parcels/my'),
  cancel:                  (id)           => api.delete(`/parcels/${id}/cancel`),
  // Company Portal
  getPending:              ()             => api.get('/parcels/pending'),
  getLateDropoffs:         ()             => api.get('/parcels/late-dropoffs'),
  getStats:                ()             => api.get('/parcels/stats'),
  confirmPayment:           (id, data)    => api.patch(`/parcels/${id}/confirm`, data),
  rejectPayment:            (id, data)    => api.patch(`/parcels/${id}/reject`, data),
  approveLateException:     (id, data)    => api.post(`/parcels/${id}/late-exception`, data),
  getPricingRules:          ()             => api.get('/parcels/pricing-rules'),
  updatePricingRules:       (data)         => api.patch('/parcels/pricing-rules', data),
  // Incidents (company_admin + super_admin)
  getIncidents:             (status)       => api.get('/parcels/incidents', { params: status ? { status } : {} }),
  resolveIncident:          (id, data)     => api.patch(`/parcels/incidents/${id}/resolve`, data),
  // Super Admin
  getAll:                  (params)       => api.get('/parcels', { params }),
  getAnalytics:            ()             => api.get('/parcels/analytics'),
};

// ── Transport Marketplace — customer side (passenger portal, main `api`
// instance / safer_session cookie, same as parcelAPI's passenger calls) ────────
export const transportAPI = {
  createRequest:   (data)       => api.post('/transport/requests', data),
  getMyRequests:   ()           => api.get('/transport/requests'),
  getRequestById:  (id)         => api.get(`/transport/requests/${id}`),
  cancelRequest:   (id)         => api.post(`/transport/requests/${id}/cancel`),
  acceptQuote:     (requestId, quoteId) => api.post(`/transport/requests/${requestId}/quotes/${quoteId}/accept`),
  getMyOrders:     ()           => api.get('/transport/orders'),
  getOrderById:    (id)         => api.get(`/transport/orders/${id}`),
  uploadPaymentProof: (id, fd)  => api.post(`/transport/orders/${id}/payment-proof`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  submitReview:    (id, data)   => api.post(`/transport/orders/${id}/review`, data),
  getBankAccounts: ()           => api.get('/transport/bank-accounts'),
};

// ── Ride Marketplace (taxi/bus/rickshaw) — customer side, same
// main `api` instance as transportAPI above. ──────────────────────────────────
export const rideAPI = {
  createRequest:  (data)       => api.post('/rides/requests', data),
  getMyRequests:  ()           => api.get('/rides/requests'),
  getRequestById: (id)         => api.get(`/rides/requests/${id}`),
  cancelRequest:  (id)         => api.post(`/rides/requests/${id}/cancel`),
  acceptOffer:    (requestId, offerId) => api.post(`/rides/requests/${requestId}/offers/${offerId}/accept`),
  getMyOrders:    ()           => api.get('/rides/orders'),
  getOrderById:   (id)         => api.get(`/rides/orders/${id}`),
  submitReview:   (id, data)   => api.post(`/rides/orders/${id}/review`, data),
};

export const companyOfficeAPI = {
  list:   ()         => api.get('/company/offices'),
  search: (q)        => api.get('/company/offices/search', { params: { q } }),
  add:    (officeId) => api.post('/company/offices', { officeId }),
  toggle: (linkId)   => api.patch(`/company/offices/${linkId}`),
  remove: (linkId)   => api.delete(`/company/offices/${linkId}`),
};

export const officeAdminAPI = {
  list:            (params)     => api.get('/admin/offices', { params }),
  get:             (id)         => api.get(`/admin/offices/${id}`),
  create:          (data)       => api.post('/admin/offices', data),
  update:          (id, data)   => api.put(`/admin/offices/${id}`, data),
  approve:         (id)         => api.patch(`/admin/offices/${id}/approve`),
  suspend:         (id)         => api.patch(`/admin/offices/${id}/suspend`),
  setCommission:   (id, data)   => api.patch(`/admin/offices/${id}/commission`, data),
  listEmployees:   (id)         => api.get(`/admin/offices/${id}/employees`),
  addEmployee:     (id, data)   => api.post(`/admin/offices/${id}/employees`, data),
  toggleEmployee:  (id, empId)  => api.patch(`/admin/offices/${id}/employees/${empId}/toggle`),
  getBookings:     (id, params) => api.get(`/admin/offices/${id}/bookings`, { params }),
  listCustomers:   (id)         => api.get(`/admin/offices/${id}/customers`),
  reassignCustomer: (customerId, newOfficeId) => api.patch(`/admin/offices/customers/${customerId}/reassign`, { newOfficeId }),
};

export const transportAdminAPI = {
  list:            (params)     => api.get('/admin/transport-providers', { params }),
  get:             (id)         => api.get(`/admin/transport-providers/${id}`),
  create:          (data)       => api.post('/admin/transport-providers', data),
  update:          (id, data)   => api.put(`/admin/transport-providers/${id}`, data),
  approve:         (id)         => api.patch(`/admin/transport-providers/${id}/approve`),
  suspend:         (id)         => api.patch(`/admin/transport-providers/${id}/suspend`),
  setCommission:   (id, data)   => api.patch(`/admin/transport-providers/${id}/commission`, data),
  listStaff:       (id)         => api.get(`/admin/transport-providers/${id}/staff`),
  addStaff:        (id, data)   => api.post(`/admin/transport-providers/${id}/staff`, data),
  toggleStaff:     (id, staffId) => api.patch(`/admin/transport-providers/${id}/staff/${staffId}/toggle`),
  listPendingPayments: ()       => api.get('/admin/transport-providers/payments/pending'),
  confirmPayment:  (id)         => api.patch(`/admin/transport-providers/payments/${id}/confirm`),
  rejectPayment:   (id, reason) => api.patch(`/admin/transport-providers/payments/${id}/reject`, { reason }),
  listBankAccounts:  ()             => api.get('/admin/transport-providers/bank-accounts'),
  createBankAccount: (data)         => api.post('/admin/transport-providers/bank-accounts', data),
  updateBankAccount: (id, data)     => api.put(`/admin/transport-providers/bank-accounts/${id}`, data),
  deleteBankAccount: (id)           => api.delete(`/admin/transport-providers/bank-accounts/${id}`),
};

export const commissionAPI = {
  // Global config
  getConfig:       ()           => api.get('/admin/commission/config'),
  setConfig:       (configs)    => api.post('/admin/commission/config', { configs }),
  getConfigHistory:()           => api.get('/admin/commission/config/history'),
  // Commission profiles
  listProfiles:    (type)       => api.get('/admin/commission/profiles', { params: type ? { type } : {} }),
  createProfile:   (data)       => api.post('/admin/commission/profiles', data),
  updateProfile:   (id, data)   => api.put(`/admin/commission/profiles/${id}`, data),
  deactivateProfile:(id)        => api.patch(`/admin/commission/profiles/${id}/deactivate`),
  assignProfile:   (data)       => api.post('/admin/commission/profiles/assign', data),
  unassignProfile: (data)       => api.post('/admin/commission/profiles/unassign', data),
  // Transactions
  getTransactions:    (params)     => api.get('/admin/commission/transactions', { params }),
  getTransaction:     (id)         => api.get(`/admin/commission/transactions/${id}`),
  reverseTransaction: (id, reason) => api.post(`/admin/commission/transactions/${id}/reverse`, { reason }),
  markPayable:        (id)         => api.patch(`/admin/commission/transactions/${id}/mark-payable`),
  getSummary:         (params)     => api.get('/admin/commission/summary', { params }),
  // Customer attribution
  listCustomers:   (params)     => api.get('/admin/commission/customers', { params }),
  lookupCustomer:  (mobile)     => api.get('/admin/commission/customers/lookup', { params: { mobile } }),
};

export default api;
